import { inject, Injectable, Type } from '@angular/core';
import { CampaignField } from '@fmnts/api/next';
import {
  filterFormControlValue,
  isAggregatedFormControl,
} from '@fmnts/common/forms';
import { deepEqual, isNil, isNonEmptyString } from '@fmnts/core';
import {
  DeclarativeFieldBuilder,
  DeclarativeForm,
  FieldControlState,
  FieldNodeInputDefinitionList,
  FieldScope,
  FormDeclaration,
  FormInputField,
  IDeclarativeFieldComponent,
  isFormInputField,
  templateExpressionRegex,
} from '@fmnts/declarative-forms';
import {
  FieldNodeName,
  FormOperatorName,
  TemplateOperatorName,
  UnknownFieldComponent,
} from '@fmnts/declarative-forms/common';
import { DeclarativeRadioContentFieldComponent } from '@fmnts/declarative-forms/radio';
import { DeclarativeTaxInputFieldComponent } from '@fmnts/declarative-forms/tax';
import { LegacyStore } from '@formunauts/next/data-access';
import { MultiIntervalAmountFieldComponent } from './components/donation-amount-field/multi-interval-amount-field.component';
import {
  AddressSearchFieldComponent,
  AmountSummaryFieldComponent,
  ButtonFieldComponent,
  CheckboxFieldComponent,
  CheckboxGroupFieldComponent,
  CheckboxGroupSummaryFieldComponent,
  ComplimentsSelectFieldComponent,
  ComplimentsSelectSummaryFieldComponent,
  DateFieldComponent,
  DateSummaryFieldComponent,
  DonationAmountFieldComponent,
  PaymentSummaryFieldComponent,
  PhoneFieldComponent,
  RadioGroupSummaryFieldComponent,
  RatingFieldComponent,
  RatingSummaryFieldComponent,
  SelectFieldComponent,
  SignatureFieldComponent,
  SponsorshipFieldComponent,
  TextBoxFieldComponent,
  TextFieldComponent,
  TextSummaryFieldComponent,
} from './components/form-fields';
import * as PhoneField from './components/phone-field/phone-field.model';

interface InitialValue {
  value: any;
  from: 'default' | 'session' | 'store';
}

/**
 * @returns
 * `true`, if `a` and `b` are considered equal
 */
function valuesEqual(a: unknown, b: unknown) {
  return (
    deepEqual(a, b) ||
    [[null, '']].some(
      ([v1, v2]) => (a === v1 && b === v2) || (a === v2 && b === v1),
    )
  );
}

/**
 * Helper function that takes the root form and returns a value
 * to be persisted so that the current form draft can be restored
 * across page reloads.
 *
 * It will persist values for enabled, dirty controls with non-null
 * values.
 */
export const transformFormValuesForStore = filterFormControlValue(
  (ctrl) =>
    ctrl.enabled &&
    ctrl.dirty &&
    // Aggregated form controls should be traversed
    (isAggregatedFormControl(ctrl) ||
      // don't store null values
      ctrl.value !== null),
);

export function transformSessionValuesForStore(
  declaration: FormDeclaration,
  form: DeclarativeForm,
): Record<string, unknown> {
  return Object.entries(form.flatValue)
    .filter(
      ([fieldKey, value]) =>
        !isNil(value) &&
        declaration.findByKey(fieldKey, isFormInputField)?.widgetOptions
          .scope === 'session',
    )
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
}

export function transformDefaultValue(
  value: unknown,
  ctx: { field: FormInputField },
): unknown {
  switch (ctx.field.widget) {
    case 'phone_number':
      return PhoneField.transformDefaultValue(value);
    case 'checkboxes':
      // TODO: do we even still need that?
      return Array.isArray(value) ? value : [];
    default:
      return value;
  }
}

/**
 * Next Field Builder
 */
@Injectable()
export class NextFieldBuilder
  implements DeclarativeFieldBuilder<CampaignField>
{
  public readonly sessionValuesStoreId = `sessionValues`;

  private readonly store = inject(LegacyStore);

  get storeId() {
    return this._storeId;
  }
  set storeId(value: string) {
    this._storeId = value;
  }
  private _storeId: string;

  public get formValuesStoreId() {
    return `formValues__${this.storeId}`;
  }

  resolveFieldComponentType(
    field: CampaignField,
  ): Type<IDeclarativeFieldComponent> {
    switch (field.widget) {
      case 'amount_summary':
        return AmountSummaryFieldComponent;
      case 'amountChoice':
        return DonationAmountFieldComponent;
      case 'multiIntervalAmountChoice':
        return MultiIntervalAmountFieldComponent;
      case 'button':
        return ButtonFieldComponent;
      case 'checkbox':
        return CheckboxFieldComponent;
      case 'checkboxes':
        return CheckboxGroupFieldComponent;
      case 'checkboxgroup_summary':
        return CheckboxGroupSummaryFieldComponent;
      case 'checkboxesWithContent':
        return ComplimentsSelectFieldComponent;
      case 'compliments_summary':
        return ComplimentsSelectSummaryFieldComponent;
      case 'customDateField':
        return DateFieldComponent;
      case 'date_summary':
        return DateSummaryFieldComponent;
      case 'googleAddressSearch':
        return AddressSearchFieldComponent;
      case 'payment_summary':
        return PaymentSummaryFieldComponent;
      case 'phone_number':
        return PhoneFieldComponent;
      case 'radioToggle':
      case 'radiosWithContent':
        return DeclarativeRadioContentFieldComponent;
      case 'radiogroup_summary':
        return RadioGroupSummaryFieldComponent;
      case 'select':
        return SelectFieldComponent;
      case 'signaturePad':
        return SignatureFieldComponent;
      case 'sponsorship':
        return SponsorshipFieldComponent;
      case 'starRating':
        return RatingFieldComponent;
      case 'rating_summary':
        return RatingSummaryFieldComponent;
      case 'taxIdentificationNumber':
        return DeclarativeTaxInputFieldComponent;
      case 'text':
        return TextFieldComponent;
      case 'text_summary':
        return TextSummaryFieldComponent;
      case 'textbox':
        return TextBoxFieldComponent;
      default:
        return UnknownFieldComponent;
    }
  }

  getFieldStyles(field: CampaignField, path: [string, string]): string {
    const [fieldsetKey, fieldKey] = path;

    // TODO: Maybe let this be sent by the backend?
    // Apply special styling for payment summary
    if (
      fieldKey === 'summary_payment_information' &&
      fieldsetKey === 'summary'
    ) {
      return 'payment_summary--signature-step';
    }

    return '';
  }

  getInitialFieldControlState<T>(
    field: CampaignField,
    path: [string, string],
  ): FieldControlState<T> {
    let initial: InitialValue = {
      value: null,
      from: 'default',
    };

    if (isFormInputField(field)) {
      initial = this._getInitialControlValue(path, field);
    }

    // Only dirty values are stored
    const dirty = initial.from === 'store';

    return {
      options: {},
      state: {
        // TODO: Refactor components to handle `undefined` / `null` correctly.
        value: initial.value ?? null,
        disabled: false,
      },
      uiState: {
        dirty,
      },
    };
  }

  getSharedFieldGraphDefinitions(
    field: CampaignField,
  ): FieldNodeInputDefinitionList | undefined {
    const { widget, key } = field;
    if (!isFormInputField(field)) {
      return undefined;
    }
    const { defaultValue } = field.widgetOptions || {};

    // Next forms allow control values to be set from interpolated
    // templates as long as the field is pristine.
    if (
      !isNonEmptyString(defaultValue) ||
      !templateExpressionRegex.test(defaultValue) ||
      widget.match(/summary/) ||
      key.match(/summary/)
    ) {
      return {};
    }

    return dynamicDefaultValueFromTemplate;
  }

  /**
   * @param path The path to the control that will be created
   * @param field The field for which a control will be created
   *
   * @returns
   * The initial value for the control, which is created for the given
   * `field`.
   */
  private _getInitialControlValue(
    path: [string, string],
    field: FormInputField,
  ): InitialValue {
    const { key } = field;

    const defaultValue = transformDefaultValue(
      field.widgetOptions.defaultValue,
      { field },
    );

    let initial: InitialValue = {
      value: defaultValue,
      from: 'default',
    };

    // Read initial value from session scope
    if (field.widgetOptions.scope === FieldScope.Session) {
      const sessionValue = this.store.get(
        `${this.sessionValuesStoreId}.${key}`,
      );
      if (sessionValue) {
        initial = {
          value: sessionValue,
          from: 'session',
        };
      }
    }

    // Read from stored values
    const storedValue = this.store.get(
      [this.formValuesStoreId, ...path].join('.'),
    );

    if (storedValue !== null) {
      if (!valuesEqual(initial.value, storedValue)) {
        initial = {
          value: storedValue,
          from: 'store',
        };
      }
    }

    return initial;
  }
}

/**
 * Graph to set a fields value to the interpolated default value,
 * as long as the field is pristine.
 */
const dynamicDefaultValueFromTemplate: FieldNodeInputDefinitionList = {
  value: {
    name: FieldNodeName.FieldProperty,
    options: {
      property: 'defaultValue',
      isWidgetProperty: true,
    },
    pipe: [
      {
        operator: TemplateOperatorName.Interpolate,
      },
      {
        operator: FormOperatorName.TakeWhilePristine,
        args: [],
      },
    ],
  },
};
